import {
  Component,
  AfterViewInit,
  OnChanges,
  Input,
  ElementRef,
} from '@angular/core';
import { ChartComponent } from '../chart.component';
import { select, Selection } from 'd3-selection';
import { axisBottom, axisLeft } from 'd3-axis';
import { scaleLinear, scaleBand } from 'd3-scale';

export interface BarChartEntry {
  value: number;
  id: string;
  type: string;
  percentage?: number;
  duration?: string;
}
export type BarChartData = BarChartEntry[];

@Component({
  selector: 'app-bar-chart-base',
  template: '',
})
export class BarChartBaseComponent
  extends ChartComponent
  implements AfterViewInit, OnChanges {
  @Input() data: BarChartData;
  @Input() colors: { [type: string]: string } | string;
  @Input() yDomain: [number, number];
  @Input() showLabels: boolean;

  @Input() avgLabel: string;

  private xScale: any;
  private yScale: any;
  private yScaleMax: number;

  constructor(protected el: ElementRef) {
    super(el);
  }

  ngAfterViewInit() {
    this.drawChart();
  }

  ngOnChanges(): void {
    if (!this.data) {
      return;
    }
    this.data = this.data.map((d) => {
      d.percentage = d.percentage ? parseFloat(d.percentage as any) : 0;
      return d;
    });

    this.drawChart();
  }

  drawChart() {
    this.updateChartDimentions();
    if (this.width && this.height && this.data) {
      this.updateDimensions();
      this.initElements();
      this.setScales();
      this.drawBars();
      this.drawAvgLine();
      this.addAxis();
    }
  }

  private updateDimensions() {
    this.height = this.height - this.margin.top - this.margin.bottom;
    this.width = this.width - this.margin.left - this.margin.right;
  }

  private initElements() {
    this.svg = select(this.chartElement)
      .append('svg')
      .attr('width', this.width + this.margin.left + this.margin.right)
      .attr('height', this.height + this.margin.top + this.margin.bottom)
      .append('g')
      .attr('transform', `translate(${this.margin.left},${this.margin.top})`);
  }

  private setScales() {
    this.xScale = scaleBand()
      .domain([...this.data.map((entry) => entry.id)])
      .range([0, this.width])
      .padding(0.2);

    const _yValuesList = this.data.map((entry) => Number(entry.percentage));
    this.yScaleMax = Math.max(..._yValuesList);
    this.yScale = scaleLinear()
      .domain(this.yDomain || [0, this.yScaleMax])
      .range([this.height, 0]);
  }

  private drawBars() {
    this.svg
      .selectAll()
      .data(this.data)
      .enter()
      .append('rect')
      .attr('fill', (d) =>
        typeof this.colors === 'string' ? this.colors : this.colors[d.type]
      )
      .attr('x', (d) => this.xScale(d.id))
      .attr('y', (d) => this.yScale(d.percentage))
      .attr('height', (d) => this.height - this.yScale(d.percentage))
      .attr('width', this.xScale.bandwidth())
      .on('click', (d, i, elements) =>
        this.clickElement.emit({ d, i, elements })
      )
      .on('mouseover', (d, i, elements) =>
        this.mouseOverElement.emit({ d, i, elements })
      )
      .on('mouseout', (d, i, elements) =>
        this.mouseOutElement.emit({ d, i, elements })
      );

    if (this.showLabels) {
      this.svg
        .selectAll()
        .data(this.data)
        .enter()
        .append('text')
        .text((d) => d.value)
        .attr('fill', '#929497')
        .attr('font-size', '10px')
        .attr('text-anchor', 'start')
        .attr(
          'transform',
          (d) =>
            `translate(${
              this.xScale(d.id) + this.xScale.bandwidth() / 2
            },${this.yScale(d.percentage)}) rotate(-45) `
        );
    }
  }

  private drawAvgLine() {
    const average =
      this.data.reduce((prev, curr) => prev + curr.percentage, 0) /
      this.data.length;
    this.svg
      .append('line')
      .attr('x1', 0)
      .attr('y1', this.yScale(average))
      .attr('x2', this.width)
      .attr('y2', this.yScale(average))
      .attr('stroke', '#dd5353');

    this.svg
      .append('text')
      .text(`${this.avgLabel} ${average.toFixed(2)}%`)
      .attr('x', this.width)
      .attr('y', this.yScale(average) - 5)
      .attr('text-anchor', 'end')
      .attr('fill', '#dd5353');
  }

  private addAxis() {
    // Add the X Axis
    this.svg
      .append('g')
      .attr('transform', `translate(0,${this.height})`)
      .call(this.prepareXAxis.bind(this))
      .selectAll('text')
      .attr('dx', '.15em')
      .attr('dy', '.15em')
      .attr('transform', 'rotate(40)')
      .style('text-anchor', 'start');

    // Add the Y Axis
    this.svg.append('g').call(this.prepareYAxis.bind(this));
  }

  private prepareXAxis(axis: Selection<SVGGElement, {}, any, {}>) {
    axis.call(axisBottom(this.xScale).tickPadding(10).tickSize(0));
    this.svg
      .append('text')
      .text('id')
      .attr('transform', `translate(${this.width + 5},${this.height + 5})`)
      .attr('font-weight', 'bold');
  }

  private prepareYAxis(axis: Selection<SVGGElement, {}, any, {}>) {
    axis.call(axisLeft(this.yScale).tickPadding(10).tickSize(0).ticks(5));
    this.svg
      .append('text')
      .text('%')
      .attr('transform', `translate(${-40},${this.height / 2})`)
      .attr('font-weight', 'bold');
  }
}
